// ============================================================
// CaseLoadingSkeleton.tsx
// The case page's CONTENT-AREA placeholder while a case is being fetched
// (cold-open, Back, or a "Full case" click before the quick-view's fetch has
// returned). It sits INSIDE the normal page shell — ViewingPane keeps the
// AppHeader (topbar portal) + the Sidebar mounted around it — so opening a
// case never blanks the topbar. Shapes mirror the real page: the green hero
// band, the grouped section nav, and the Overview's first cards.
// ============================================================
import React from 'react'
import { CASE_CARD } from './caseUi'

const Bar: React.FC<{ className: string }> = ({ className }) => (
  <div className={`rounded-[6px] bg-casevo-border-soft animate-pulse ${className}`} />
)

const CaseLoadingSkeleton: React.FC = () => (
  <div role="status" aria-live="polite" aria-label="Loading case" className="leading-[normal]">
    {/* Hero band — same gradient + radius as CaseHero, content as soft bars. */}
    {/* Vertical rhythm matches CaseHero (tags row · title · subtitle · fact
        tiles) so the swap to the real hero doesn't jump the page. */}
    <div className="rounded-[14px] overflow-hidden bg-gradient-to-br from-[#1a3a2a] to-[#2d6a4f] dark:from-[#1a4230] dark:to-[#2d6a4f] px-[28px] pt-[28px] pb-[20px]">
      <div className="h-[22px] w-[260px] max-w-full rounded-[6px] bg-white/10 animate-pulse" />
      <div className="mt-[30px] h-[30px] w-[400px] max-w-full rounded-[8px] bg-white/20 animate-pulse" />
      <div className="mt-[10px] h-[15px] w-[240px] max-w-full rounded-[6px] bg-white/15 animate-pulse" />
      <div className="mt-[22px] grid grid-cols-2 md:grid-cols-4 gap-[10px]">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-[72px] rounded-[10px] bg-white/10 animate-pulse" />)}
      </div>
    </div>

    <div className="mt-[14px] md:mt-[22px] grid grid-cols-1 md:grid-cols-[232px_minmax(0,1fr)] gap-[16px] md:gap-[26px] items-start">
      {/* Section nav */}
      <div className="hidden md:flex flex-col gap-[8px] px-[12px]">
        <Bar className="h-[10px] w-[70px] mb-[4px]" />
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <Bar key={i} className={`h-[30px] w-full ${i === 3 ? 'mt-[12px]' : ''}`} />)}
      </div>
      {/* Content */}
      <div className="min-w-0 flex flex-col gap-[16px]">
        <div className={`${CASE_CARD} p-[20px]`}>
          <Bar className="h-[12px] w-[120px]" />
          <div className="mt-[14px] grid grid-cols-2 md:grid-cols-4 gap-[12px]">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-[64px] rounded-[10px] bg-casevo-input animate-pulse" />)}
          </div>
        </div>
        <div className={`${CASE_CARD} p-[20px] flex flex-col gap-[10px]`}>
          <Bar className="h-[12px] w-[150px]" />
          {[0, 1, 2].map((i) => <div key={i} className="h-[36px] rounded-[8px] bg-casevo-input animate-pulse" />)}
        </div>
      </div>
    </div>
    <span className="sr-only">Loading case…</span>
  </div>
)

export default CaseLoadingSkeleton
